<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <div class="absolute inset-0 flex flex-col overflow-y-hidden">
      <div class="flex-1 overflow-y-auto">
        <header class="space-y-1 py-6 px-4 bg-cyan-700">
          <div class="flex items-center justify-between">
            <h2 class="text-lg leading-7 font-medium text-white">
              <span v-if="formAction === 'add'">เพิ่ม Device</span>
              <span v-if="formAction === 'edit'">ตั้งค่า Device</span>
            </h2>
            <button
              @click="onClose"
              aria-label="Close panel"
              class="
                text-cyan-200
                hover:text-white
                transition
                ease-in-out
                duration-150
              "
            >
              <svg
                class="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div>
            <p class="text-sm leading-5 text-cyan-300">
              <span v-if="formAction === 'add'">เพิ่ม Device ใหม่</span>
              <span v-if="formAction === 'edit'"
                >ตั้งค่าสถานะ Device และลูกค้าที่เป็นเจ้าของ Device</span
              >
            </p>
          </div>
        </header>

        <!-- Error Message -->
        <div
          v-if="errors.length > 0"
          class="px-4 py-3 bg-red-600 bg-opacity-20 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-red-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-base font-semibold text-red-800 flex-1 px-3">
            {{ errors[0] }}
          </p>
          <button @click="errors = []" type="button" class="focus:outline-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-red-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
        <!-- End Error Message -->

        <!-- Add Form -->
        <div v-if="formAction === 'add'" class="p-4 space-y-6">
          <div>
            <label class="block text-base font-medium text-gray-700">
              เลือก Device Model
              <span class="text-xl text-red-600">*</span>
            </label>
            <ValidationProvider
              name="Device Model"
              rules="required"
              v-slot="{ errors }"
            >
              <AppAutoComplete
                v-model="hardware.hardware_model"
                :isSearch="true"
                :options="hardwareModels"
                label="เลือก Device Model"
                item-text-key="title"
                item-value-key="hardware_model_code"
                class="mt-1"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <label class="block text-base font-medium text-gray-700">
              Device S/N
              <span class="text-xl text-red-600">*</span>
            </label>
            <ValidationProvider
              name="Device S/N"
              rules="required"
              v-slot="{ errors }"
            >
              <AppInput
                v-model.trim="hardware.hardware_serial_number"
                type="text"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="sr-only">{{ isDataChange }}</div>
        </div>
        <!-- End Add Form -->

        <div v-if="formAction === 'edit'" class="p-4">
          <h2 class="mb-2 text-base font-semibold text-gray-900">
            ข้อมูลทั่วไป
          </h2>
          <div class="space-y-6">
            <div>
              <AppInput
                v-model="hardware.hardware_serial_number"
                label="Device S/N"
                type="text"
                disabled
                className="cursor-not-allowed"
              />
            </div>

            <div>
              <label class="block text-base font-medium text-gray-700">
                เลือกชื่อลูกค้า
              </label>
              <AppAutoComplete
                v-model="hardware.company"
                :isSearch="true"
                :options="filterCompanies"
                label="เลือกชื่อลูกค้า"
                item-text-key="title_th"
                item-value-key="company_uuid"
                class="mt-1"
                @input="handleOnCompanyChange"
              />
            </div>

            <div>
              <label class="block text-base font-medium text-gray-700"
                >เลือกสถานะของ Device<span class="text-xl text-red-600"
                  >*</span
                ></label
              >
              <ValidationProvider
                name="สถานะอาร์ดแวร์"
                rules="required"
                v-slot="{ errors }"
              >
                <AppAutoComplete
                  v-model="hardware.hardware_status"
                  :isSearch="false"
                  :options="filterHardwareStatus"
                  label="เลือกสถานะอาร์ดแวร์"
                  item-text-key="title_th"
                  item-value-key="hardware_status_uuid"
                  class="mt-1"
                />
                <span class="text-base text-red-600">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <div>
              <label class="block text-base font-medium text-gray-700">
                เลือก Device Model
                <span class="text-xl text-red-600">*</span>
              </label>
              <ValidationProvider
                name="Device Model"
                rules="required"
                v-slot="{ errors }"
              >
                <AppAutoComplete
                  v-model="hardware.hardware_model"
                  :isSearch="true"
                  :options="hardwareModels"
                  label="เลือก Device Model"
                  item-text-key="title"
                  item-value-key="hardware_model_code"
                  class="mt-1"
                  :isDisabled="Boolean(currentHardware.company)"
                />
                <span class="text-base text-red-600">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <div>
              <AppTextarea v-model="hardware.note" label="ใส่โน็ตเพิ่มเติม" />
            </div>

            <div class="sr-only">{{ isDataChange }}</div>
          </div>

          <h2 class="mt-6 mb-2 text-base font-semibold text-gray-900">
            การลงทะเบียน Device
          </h2>
          <div class="flex items-center space-x-2">
            <svg
              v-if="hardware.is_registered"
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-green-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-gray-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z"
              />
            </svg>
            <span>
              {{
                hardware.is_registered
                  ? 'ลงทะเบียนสำเร็จแล้ว'
                  : 'รอการลงทะเบียนจาก Device'
              }}
            </span>
          </div>
          <button
            v-if="hardware.is_registered"
            @click="handleUnRegister"
            :disabled="loading"
            :class="{ 'cursor-not-allowed': loading }"
            type="button"
            class="
              w-full
              inline-flex
              items-center
              justify-center
              rounded-md
              border border-gray-300
              shadow-sm
              px-4
              py-1
              bg-white
              text-base
              font-medium
              text-gray-700
              hover:bg-gray-50
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-cyan-500
              sm:w-auto
              mt-2
            "
          >
            <svg
              v-if="loading"
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-cyan-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            ถอนการลงทะเบียน
          </button>
          <button
            v-else
            @click="handleCheckStatus"
            :disabled="loading"
            :class="{ 'cursor-not-allowed': loading }"
            type="button"
            class="
              w-full
              inline-flex
              items-center
              justify-center
              rounded-md
              border border-gray-300
              shadow-sm
              px-4
              py-1
              bg-white
              text-base
              font-medium
              text-gray-700
              hover:bg-gray-50
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-cyan-500
              sm:w-auto
              mt-2
            "
          >
            <svg
              v-if="loading"
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-cyan-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            อัพเดทสถานะ
          </button>
        </div>
      </div>

      <div>
        <div class="p-4 border-t flex justify-end">
          <div
            class="flex items-end"
            v-if="!isDataChange && formAction === 'edit'"
          >
            <p
              v-if="hasCustomerUseDevice"
              class="text-sm text-red-500 font-medium mr-4"
            >
              ไม่สามารถลบได้เนื่องจากลูกค้ากำลังใช้งาน
            </p>
            <button
              @click="onDelete"
              :disabled="loading || hasCustomerUseDevice"
              type="button"
              :class="
                loading || hasCustomerUseDevice
                  ? 'cursor-not-allowed bg-red-300'
                  : 'bg-red-600 hover:bg-red-700'
              "
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                text-base
                font-medium
                text-white
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-red-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span class="ml-2">ลบ</span>
            </button>
          </div>

          <div v-else class="space-x-4">
            <button
              @click="onCancel"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-gray-300
                shadow-sm
                px-4
                py-2
                bg-white
                text-base
                font-medium
                text-gray-700
                hover:bg-gray-50
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:ml-3 sm:w-auto
              "
            >
              ยกเลิก
            </button>
            <button
              v-if="formAction === 'add'"
              @click="handleSubmit(onSubmit)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              ตกลง
            </button>
            <button
              v-if="formAction === 'edit'"
              @click="handleSubmit(onSave)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              บันทึก
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- confirm delete -->
    <ModalEditCustomerHardware
      :show="isEditCustomer"
      :loading="editCustomerLoading"
      :item="editCustomerItem"
      :errors="editCustomerErrors"
      @clear="editCustomerErrors = []"
      @cancel="onCancelEditCustomerHardware"
      @submit="onSubmitEditCustomerHardware"
    />
    <!-- end confirm delete -->
  </ValidationObserver>
</template>

<script>
import AppInput from '@/components/AppInput'
import AppTextarea from '@/components/AppTextarea'
import AppAutoComplete from '@/components/AppAutoComplete'
import ModalEditCustomerHardware from '@/components/ModalEditCustomerHardware'
import { extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'

// No message specified.
// extend('required', required)

// Override the default message.
extend('required', {
  ...required,
  message: 'จำเป็นต้องระบุ {_field_}',
})

export default {
  name: 'FormHardware',

  props: {
    formAction: {
      type: String,
      required: true,
    },
    currentHardware: {
      type: Object,
    },
    hardware: {
      type: Object,
    },
  },

  components: {
    AppInput,
    AppTextarea,
    AppAutoComplete,
    ModalEditCustomerHardware,
  },

  data() {
    return {
      loading: false,
      errors: [],
      isEditCustomer: false,
      editCustomerLoading: false,
      editCustomerItem: {},
      editCustomerErrors: [],
      timeout: null,
    }
  },

  computed: {
    hasCustomerUseDevice() {
      return Boolean(this.currentHardware.company?.company_uuid)
    },
    isDataChange() {
      if (
        this.hardware.note !== this.currentHardware.note ||
        this.hardware.company?.company_uuid !==
          this.currentHardware.company?.company_uuid ||
        this.hardware.hardware_status?.hardware_status_uuid !==
          this.currentHardware.hardware_status?.hardware_status_uuid ||
        this.hardware.hardware_model?.hardware_model_code !==
          this.currentHardware.hardware_model?.hardware_model_code ||
        this.hardware.hardware_serial_number !==
          this.currentHardware.hardware_serial_number
      ) {
        this.$emit('isDataChange', true)
        return true
      }
      this.$emit('isDataChange', false)
      return false
    },

    customers() {
      return this.$store.state.customer.customers
    },

    hardwareStatus() {
      return this.$store.state.hardware.hardwareStatus
    },

    filterCompanies() {
      if (this.currentHardware.company) {
        return this.customers.filter(
          (customer) =>
            customer.company_uuid === this.currentHardware.company.company_uuid
        )
      } else {
        return this.customers
      }
    },

    filterHardwareStatus() {
      if (this.hardware.company) {
        return this.hardwareStatus.filter(
          (item) => item.is_only_use_for_customer_assigned
        )
      } else {
        return this.hardwareStatus.filter(
          (item) => !item.is_only_use_for_customer_assigned
        )
      }
    },

    hardwareModels() {
      return this.$store.state.hardware.hardwareModels
    },
  },

  methods: {
    onCancelEditCustomerHardware() {
      this.isEditCustomer = false
      this.editCustomerErrors = []
    },
    async onSubmitEditCustomerHardware(hardware) {
      try {
        const hardwareUpdate = {
          hardware_uuid: hardware.hardware_uuid,
          hardware_serial_number: hardware.hardware_serial_number,
          hardware_status_uuid: hardware.hardware_status.hardware_status_uuid,
          hardware_model_code: hardware.hardware_model.hardware_model_code,
          company_uuid: hardware.company ? hardware.company.company_uuid : null,
          hardware_code: hardware.hardware_code,
          note: hardware.note || null,
        }

        this.editCustomerLoading = true

        await this.$store.dispatch('hardware/editHardware', hardwareUpdate)

        this.editCustomerLoading = false
        this.isEditCustomer = false

        this.timeout = setTimeout(() => {
          const notification = {
            type: 'success',
            message: 'Your hardware has been updated!',
          }
          this.$store.dispatch('notification/add', notification)

          this.$emit('isDataChange', false)
          this.onClose()
        }, 300)
      } catch (error) {
        this.editCustomerLoading = false

        if (error.response && error.response.data) {
          this.editCustomerErrors.push(
            error.response.data.message || 'Something went wrong.'
          )
          return
        }

        console.log(error.response)
      }
    },
    handleOnCompanyChange() {
      this.$emit('onHardwareCompanyChange')
      // if (!this.hardware.company && this.hardware.hardware_status) {
      //   this.$emit('onHardwareCompanyChange')
      // }
    },
    onCancel() {
      this.$emit('onCancel')
    },

    onClose() {
      this.$emit('onClose')
    },

    async handleCheckStatus() {
      try {
        const hardware_uuid = this.hardware.hardware_uuid

        this.loading = true

        const hardware = await this.$store.dispatch(
          'hardware/checkStatusHardware',
          hardware_uuid
        )

        if (hardware) {
          const notification = {
            type: 'success',
            message: 'Your hardware has been updated!',
          }
          this.$store.dispatch('notification/add', notification)

          this.$emit('isDataChange', false)
          this.$emit('updateHardware', hardware)
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(error.response.data.message)
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    async handleUnRegister() {
      try {
        const hardware_uuid = this.hardware.hardware_uuid

        this.loading = true

        const hardware = await this.$store.dispatch(
          'hardware/unRegisterHardware',
          hardware_uuid
        )

        const notification = {
          type: 'success',
          message: 'Your hardware has been updated!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.$emit('updateHardware', hardware)
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(error.response.data.message)
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    async onSubmit() {
      try {
        const newHardware = {
          hardware_model_code: this.hardware.hardware_model.hardware_model_code,
          hardware_serial_number: this.hardware.hardware_serial_number,
        }

        this.loading = true

        await this.$store.dispatch('hardware/addHardware', newHardware)

        const notification = {
          type: 'success',
          message: 'Your hardware has been added!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(error.response.data.message)
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    async onSave() {
      if (this.currentHardware.company && !this.hardware.company) {
        this.isEditCustomer = true
        this.editCustomerItem = { ...this.hardware }
        return
      }

      try {
        const hardwareUpdate = {
          ...this.currentHardware,
          hardware_uuid: this.hardware.hardware_uuid,
          hardware_serial_number: this.hardware.hardware_serial_number,
          hardware_status_uuid:
            this.hardware.hardware_status.hardware_status_uuid,
          hardware_model_code: this.hardware.hardware_model.hardware_model_code,
          company_uuid: this.hardware.company
            ? this.hardware.company.company_uuid
            : null,
          hardware_code: this.hardware.hardware_code,
          note: this.hardware.note || null,
        }

        this.loading = true

        await this.$store.dispatch('hardware/editHardware', hardwareUpdate)

        const notification = {
          type: 'success',
          message: 'Your hardware has been updated!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(error.response.data.message)
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    onDelete() {
      this.$emit('onDelete', this.hardware)
    },
  },

  beforeDestroy() {
    clearTimeout(this.timeout)
  },
}
</script>
